// noinspection JSSuspiciousNameCombination

import {
    getBevelBottom,
    getHorizontalAnchorWidth, getLidSize, getLidSizes,
    getVerticalAnchorWidth, LidSize,
} from "../../domain/lid";
import {getVaultSize} from "../../domain/vault";
import {supplementaryCoverFunctions} from "../../domain/subfloorLid";
import {getManholeClearMeasure} from "../../domain/intermediateCeiling";
import {getClosureSizes} from "../../domain/manhole";
import {getLidToIntermediateCeilingSpacing} from "../../domain/composition";
import {Plan} from "../../../types";
import {VaultSize} from "../../domain/vaultSize";

interface Position {
    x: number
    y: number
    z: number
}

const getMeasurementPositions = (plan: Plan): Position[] => {
    const vaultSize = getVaultSize(plan, true)
    const lidSize = getLidSize(plan)
    const lidOuterCorners = (): Position[] => {
        const getLeft = () => lidSize.left + getHorizontalAnchorWidth(plan, plan.lid.manholes[0])
        const getRight = () => lidSize.right - getHorizontalAnchorWidth(plan, plan.lid.manholes[0])
        const getFront = () => lidSize.front + getVerticalAnchorWidth(plan, plan.lid.manholes[0])
        const getBack = () => lidSize.back - getVerticalAnchorWidth(plan, plan.lid.manholes[0])
        const getTop = () => getBevelBottom(plan)

        return [
            {x: getLeft(), y: getFront(), z: getTop()},
            {x: getRight(), y: getFront(), z: getTop()},
            {x: getRight(), y: getBack(), z: getTop()},
            {x: getLeft(), y: getBack(), z: getTop()}
        ]
    }
    const vaultOuterCorners = (): Position[] => {
        const getTop = () => lidSize.height

        return [
            {x: vaultSize.left, y: vaultSize.front, z: getTop()},
            {x: vaultSize.right, y: vaultSize.front, z: getTop()},
            {x: vaultSize.right, y: vaultSize.back, z: getTop()},
            {x: vaultSize.left, y: vaultSize.back, z: getTop()},
        ]
    }
    const getTop = () => lidSize.height
    const toRect = (size: any): { x: number, y: number, width: number, height: number } =>
        ({...size, width: size.length, height: size.width})

    const corners = (rect: { x: number, y: number, width: number, height: number }): Position[] => {
        return [
            {x: rect.x, y: rect.y, z: getTop()},
            {x: rect.x, y: rect.y + rect.height, z: getTop()},
            {x: rect.x + rect.width, y: rect.y, z: getTop()},
            {x: rect.x + rect.width, y: rect.y + rect.height, z: getTop()}
        ]
    }

    const getA15B125D400E600Positions = (): Position[] => {
        return [
            ...lidOuterCorners().map(c => ({
                ...c,
                z: plan.lid.collar === 'without'
                    ? lidSize.height - (
                    plan.lid.type === 'A15' ? 8
                        : plan.lid.type === 'B125' ? 7.5
                            : plan.lid.type === 'D400' ? 6.5
                                : plan.lid.type === 'E600' ? 11
                                    : 0
                )
                    : c.z
            })),
            ...((plan.lid.manholes ?? []).reduce((acc: Position[], curr) =>
                [...acc, ...corners(curr)], [] as Position[])),
            ...((plan.lid.blockOuts ?? []).reduce((acc: Position[], curr) =>
                [...acc, ...corners(curr)], [] as Position[])),
            ...((plan.lid.circularBlockOuts ?? []).reduce(
                (acc: Position[], curr) => [...acc, {x: curr.x, y: curr.y, z: getTop()}],
                [] as Position[]
            ))
        ]
    }
    const getSubFloorPositions = (): Position[] => {
        return [
            ...(
                (plan.lid.manholes ?? []).length === 1
                    ? corners((plan.lid.manholes ?? [])[0]).map(p => ({
                        ...p,
                        z: getClosureSizes((plan.lid.manholes ?? [])[0].type).thickness
                    }))
                    : lidOuterCorners()
            ),
            ...(
                !!plan.vault
                    ? vaultOuterCorners().map(p => ({...p, z: 0}))
                    : []
            ),
            ...(
                supplementaryCoverFunctions.has(plan, 'left')
                    ? corners(toRect(supplementaryCoverFunctions.getSize(plan, 'left')))
                    : []
            ),
            ...(
                supplementaryCoverFunctions.has(plan, 'right')
                    ? corners(toRect(supplementaryCoverFunctions.getSize(plan, 'right')))
                    : []
            ),
            ...(
                supplementaryCoverFunctions.has(plan, 'front')
                    ? corners(toRect(supplementaryCoverFunctions.getSize(plan, 'front')))
                    : []
            ),
            ...(
                supplementaryCoverFunctions.has(plan, 'back')
                    ? corners(toRect(supplementaryCoverFunctions.getSize(plan, 'back')))
                    : []
            )
        ]
    }
    const getLidOnIntermediateCeilingPositions = (): Position[] => {
        const getManholePositions = (manhole: any): any =>
            corners(getManholeClearMeasure(plan, manhole)).map(
                p => ({
                    ...p,
                    z: plan.lid.type === 'subfloor'
                        ? getClosureSizes((plan.lid.manholes ?? [])[0].type).thickness
                        : p.z
                })
            )

        return [
            ...plan.lid.manholes.map(m =>
                getManholePositions(m)).reduce((acc: Position[], curr) => [...acc, ...curr], []
            ),
            ...vaultOuterCorners().map(p => ({...p, z: -getLidToIntermediateCeilingSpacing(plan)}))
        ]
    }

    return !!plan.intermediateCeiling
        ? getLidOnIntermediateCeilingPositions()
        : plan.lid.type === 'A15' || plan.lid.type === 'B125' || plan.lid.type === 'D400' || plan.lid.type === 'E600'
            ? getA15B125D400E600Positions()
            : plan.lid.type === 'subfloor'
                ? getSubFloorPositions()
                : []
}
const getExtensionLineLimits = (plan: Plan) => {
    const vaultSize = getVaultSize(plan, true)
    const manhole = (plan.lid.manholes ?? []).length === 1 ? (plan.lid.manholes ?? [])[0] : null
    const closureThickness = () => manhole ? getClosureSizes(manhole.type).thickness : 0


    const getLidOnIntermediateCeilingLimits = () => {
        const lidSizes = getLidSizes(plan)
        const getVaultLimits = (vaultSize: VaultSize): any[] =>
            [
                {
                    from: {x: vaultSize.left, y: vaultSize.front},
                    to: {x: vaultSize.left, y: vaultSize.back}
                },
                {
                    from: {x: vaultSize.left, y: vaultSize.front},
                    to: {x: vaultSize.right, y: vaultSize.front}
                },
            ]
        const getLidLimits = (lidSize: LidSize): any[] => [
            {
                from: {x: lidSize.left, y: lidSize.front},
                to: {x: lidSize.left, y: lidSize.back}
            },
            {
                from: {x: lidSize.left, y: lidSize.front},
                to: {x: lidSize.right, y: lidSize.front}
            },
            {
                from: {
                    x: lidSize.left,
                    z: plan.lid.type === 'subfloor' ? closureThickness() : lidSize.top
                },
                to: {
                    x: lidSize.right,
                    z: plan.lid.type === 'subfloor' ? closureThickness() : lidSize.top
                }
            },
            {
                from: {
                    y: lidSize.front,
                    z: plan.lid.type === 'subfloor' ? closureThickness() : lidSize.top
                },
                to: {
                    y: lidSize.back,
                    z: plan.lid.type === 'subfloor' ? closureThickness() : lidSize.top
                }
            }
        ]

        return [
            ...getVaultLimits(vaultSize),
            ...lidSizes.map(l => getLidLimits(l)).reduce((acc: any[], curr) => [...acc, ...curr], []),
        ]
    }
    const getA15B125D400E600Limits = () => {

        const lidSize = getLidSize(plan)
        return [
            {
                from: {
                    x: lidSize.left + getHorizontalAnchorWidth(plan, plan.lid.manholes[0]),
                    y: lidSize.front
                },
                to: {
                    x: lidSize.left + getHorizontalAnchorWidth(plan, plan.lid.manholes[0]),
                    y: lidSize.back
                }
            },
            {
                from: {
                    x: lidSize.left,
                    y: lidSize.front + getVerticalAnchorWidth(plan, plan.lid.manholes[0])
                },
                to: {
                    x: lidSize.right,
                    y: lidSize.front + getVerticalAnchorWidth(plan, plan.lid.manholes[0])
                }
            }
        ]
    }
    const getSubFloorLimits = () => {
        const manholeLeft = (plan.lid.manholes ?? []).length === 1
            ? (plan.lid.manholes ?? [])[0].x : 0
        const manholeRight = (plan.lid.manholes ?? []).length === 1
            ? ((plan.lid.manholes ?? [])[0].width + manholeLeft) : 0
        const manholeFront = (plan.lid.manholes ?? []).length === 1
            ? (plan.lid.manholes ?? [])[0].y : 0
        const manholeBack = (plan.lid.manholes ?? []).length === 1
            ? ((plan.lid.manholes ?? [])[0].height + manholeFront) : 0

        return [
            {from: {x: manholeLeft, y: manholeFront}, to: {x: manholeLeft, y: manholeBack}},
            {from: {x: manholeLeft, y: manholeFront}, to: {x: manholeRight, y: manholeFront}},
            {from: {x: manholeLeft, z: closureThickness()}, to: {x: manholeRight, z: closureThickness()}},
            {from: {y: manholeFront, z: closureThickness()}, to: {y: manholeBack, z: closureThickness()}},
            ...(
                supplementaryCoverFunctions.has(plan, 'left')
                    ? [
                        {
                            from: {
                                x: supplementaryCoverFunctions.getLeft(plan, 'left'),
                                y: supplementaryCoverFunctions.getFront(plan, 'left')
                            },
                            to: {
                                x: supplementaryCoverFunctions.getLeft(plan, 'left'),
                                y: supplementaryCoverFunctions.getBack(plan, 'left')
                            }
                        },
                        {
                            from: {
                                y: supplementaryCoverFunctions.getFront(plan, 'left'),
                                z: supplementaryCoverFunctions.getTop(plan, 'left')
                            },
                            to: {
                                y: supplementaryCoverFunctions.getBack(plan, 'left'),
                                z: supplementaryCoverFunctions.getTop(plan, 'left')
                            }
                        }
                    ]
                    : []
            ),
            ...(
                supplementaryCoverFunctions.has(plan, 'right')
                    ? [
                        {
                            from: {
                                y: supplementaryCoverFunctions.getFront(plan, 'right'),
                                z: supplementaryCoverFunctions.getTop(plan, 'right')
                            },
                            to: {
                                y: supplementaryCoverFunctions.getBack(plan, 'right'),
                                z: supplementaryCoverFunctions.getTop(plan, 'right')
                            }
                        }
                    ]
                    : []
            ),
            ...(
                supplementaryCoverFunctions.has(plan, 'front')
                    ? [
                        {
                            from: {
                                x: supplementaryCoverFunctions.getLeft(plan, 'front'),
                                y: supplementaryCoverFunctions.getFront(plan, 'front')
                            },
                            to: {
                                x: supplementaryCoverFunctions.getRight(plan, 'front'),
                                y: supplementaryCoverFunctions.getFront(plan, 'front')
                            }
                        },
                        {
                            from: {
                                x: supplementaryCoverFunctions.getLeft(plan, 'front'),
                                z: supplementaryCoverFunctions.getTop(plan, 'front')
                            },
                            to: {
                                x: supplementaryCoverFunctions.getRight(plan, 'front'),
                                z: supplementaryCoverFunctions.getTop(plan, 'front')
                            }
                        }
                    ]
                    : []
            ),
            ...(
                supplementaryCoverFunctions.has(plan, 'back')
                    ? [
                        {
                            from: {
                                x: supplementaryCoverFunctions.getLeft(plan, 'back'),
                                z: supplementaryCoverFunctions.getTop(plan, 'back')
                            },
                            to: {
                                x: supplementaryCoverFunctions.getRight(plan, 'back'),
                                z: supplementaryCoverFunctions.getTop(plan, 'back')
                            }
                        }
                    ]
                    : []
            ),
            ...(!!plan.vault ? [
                {
                    from: {x: vaultSize.left, y: vaultSize.front},
                    to: {x: vaultSize.left, y: vaultSize.back}
                },
                {
                    from: {x: vaultSize.left, y: vaultSize.front},
                    to: {x: vaultSize.right, y: vaultSize.front}
                }
            ] : [])
        ];
    }

    return !!plan.intermediateCeiling
        ? getLidOnIntermediateCeilingLimits()
        : plan.lid.type === 'A15' || plan.lid.type === 'B125' || plan.lid.type === 'D400' || plan.lid.type === 'E600'
            ? getA15B125D400E600Limits()
            : plan.lid.type === 'subfloor'
                ? getSubFloorLimits()
                : []
}

export const getGroundViewLeftMeasurementPositions: (plan: Plan) => Position[] = (plan: Plan) => {
    const filterRelevant = (positions: Position[]): Position[] => {
        const map = positions.reduce((acc: Map<number, Position>, curr: Position) => {
            if (!acc.has(curr.y) || acc.get(curr.y)!.x > curr.x) {
                acc.set(curr.y, curr)
            }
            return acc
        }, new Map<number, Position>())

        return Array.from(map.values())
    }
    const applyLimits = (positions: Position[], limits: any[]): Position[] => {
        const getRelevantLimits = (y: number) => limits.filter((l: any) =>
            Number.isFinite(l.from.x)
            && l.from.x === l.to.x
            && l.from.y <= y && l.to.y >= y
        )
        const getLimitedX = (pos: Position) => {
            const relevant = getRelevantLimits(pos.y)
            return Math.min(pos.x, ...(relevant.length ? relevant.map(l => l.from.x) : [pos.x]))
        }

        return positions.map(p => ({...p, x: getLimitedX(p)}))
    }

    return applyLimits(
        filterRelevant(
            getMeasurementPositions(plan)
        ),
        getExtensionLineLimits(plan)
    )

}
export const getGroundViewBottomMeasurementPositions = (plan: Plan): Position[] => {
    const filterRelevant = (positions: Position[]): Position[] => {
        const map = positions.reduce((acc: Map<number, Position>, curr: Position) => {
            if (!acc.has(curr.x) || acc.get(curr.x)!.y > curr.y) {
                acc.set(curr.x, curr)
            }
            return acc
        }, new Map<number, Position>())

        return Array.from(map.values())
    }
    const applyLimits = (positions: Position[], limits: any[]): Position[] => {
        const getRelevantLimits = (x: number) => limits.filter((l: any) =>
            Number.isFinite(l.from.y)
            && l.from.y === l.to.y
            && l.from.x <= x && l.to.x >= x
        )
        const getLimitedY = (x: number, y: number) => {
            const relevant = getRelevantLimits(x)
            return Math.min(y, ...(relevant.length ? relevant.map(l => l.from.y) : [y]))
        }

        return positions.map(p => ({...p, y: getLimitedY(p.x, p.y)}))
    }

    return applyLimits(
        filterRelevant(
            getMeasurementPositions(plan)
        ),
        getExtensionLineLimits(plan)
    )

}
export const getElevationViewTopMeasurementPositions = (plan: Plan, side: any): Position[] => {
    const filterRelevant = (positions: Position[]): Position[] => {
        const getHorizontalAxisPosition = (pos: Position) => side === 'front' || side === 'back' ? pos.x : pos.y
        const map = positions.reduce((acc: Map<number, Position>, curr: Position) => {
            const key = getHorizontalAxisPosition(curr)
            if (!acc.has(key) || acc.get(key)!.z < curr.z) {
                acc.set(key, curr)
            }
            return acc
        }, new Map<number, Position>())

        return Array.from(map.values())
    }
    const applyLimits = (positions: Position[], limits: any[]): Position[] => {
        const getHorizontalAxisPosition = (pos: Position) => side === 'front' || side === 'back' ? pos.x : pos.y

        const getRelevantLimits = (pos: Position) => limits.filter((l: any) =>
            Number.isFinite(l.from.z)
            && l.from.z === l.to.z
            && getHorizontalAxisPosition(l.from) <= getHorizontalAxisPosition(pos)
            && getHorizontalAxisPosition(l.to) >= getHorizontalAxisPosition(pos)
        )
        const getLimitedZ = (pos: Position) => {
            const relevant = getRelevantLimits(pos)
            return Math.max(pos.z, ...(relevant.length ? relevant.map((l: any) => l.from.z) : [pos.z]))
        }

        return positions.map(p => ({...p, z: getLimitedZ(p)}))
    }

    return applyLimits(
        filterRelevant(
            getMeasurementPositions(plan)
        ),
        getExtensionLineLimits(plan)
    )
}
